import type { ThemeConfig } from '../../themes/types';
import { localeManager, t } from '../../services/locale';
import { themeManager } from '../../services/theme';
import type { ManagerRegistry } from '../../core/manager-registry';
import { BimTree } from '../tree';
import type { TreeNodeConfig } from '../tree/types';
import type { ComponentTreeDrawerTabComponent } from './types';
import type { ModelViewTree, ViewItem } from '../engine/types';

type ViewCategory = 'ceilingPlans' | 'elevations' | 'floorPlans' | 'sections';

const CATEGORY_LABEL_MAP: Record<ViewCategory, string> = {
    ceilingPlans: 'viewTab.category.ceilingPlans',
    elevations: 'viewTab.category.elevations',
    floorPlans: 'viewTab.category.floorPlans',
    sections: 'viewTab.category.sections',
};

function transformViewTreeData(models: ModelViewTree[]): TreeNodeConfig[] {
    if (!models || models.length === 0) return [];

    const result: TreeNodeConfig[] = models.map((model): TreeNodeConfig => {
        const viewCategories: TreeNodeConfig[] = [];

        (Object.keys(model.views) as ViewCategory[]).forEach((category) => {
            const items = model.views[category];
            if (!items || items.length === 0) return;

            viewCategories.push({
                id: `${model.url}::${category}`,
                label: t(CATEGORY_LABEL_MAP[category]),
                clickAction: 'expand',
                children: items.map((item: ViewItem): TreeNodeConfig => ({
                    id: `${model.url}::${category}::${item.id}`,
                    label: item.name,
                    data: {
                        url: item.url,
                        viewId: item.id,
                    },
                })),
            });
        });

        return {
            id: model.url,
            label: model.name || model.url,
            expanded: true,
            clickAction: 'expand',
            children: viewCategories,
        };
    });

    // 在最前面插入"默认三维"固定节点
    result.unshift({
        id: '__default_3d__',
        label: t('viewTab.default3D'),
        clickAction: 'select',
        data: { isDefault3D: true },
    });

    return result;
}

export class ComponentTreeDrawerViewTab implements ComponentTreeDrawerTabComponent {
    public readonly element: HTMLElement;

    private readonly registry: ManagerRegistry | null;
    private readonly mountElement: HTMLElement;
    private unsubscribeLocale: (() => void) | null = null;
    private unsubscribeTheme: (() => void) | null = null;
    private tree: BimTree | null = null;
    private refreshToken = 0;
    private isRendering = false;
    private pendingRefresh = false;

    constructor(registry: ManagerRegistry | null) {
        this.registry = registry;
        this.element = document.createElement('section');
        this.element.className = 'component-tree-drawer-view';
        this.element.dataset.tab = 'view';

        this.mountElement = document.createElement('div');
        this.mountElement.className = 'component-tree-drawer-view-mount';
        this.element.appendChild(this.mountElement);
    }

    public init(): void {
        this.setTheme(themeManager.getTheme());
        this.unsubscribeLocale = localeManager.subscribe(() => this.setLocales());
        this.unsubscribeTheme = themeManager.subscribe((theme) => this.setTheme(theme));
    }

    public getElement(): HTMLElement {
        return this.element;
    }

    public onShow(): void {
        void this.refresh();
    }

    public setTheme(theme: ThemeConfig): void {
        const style = this.element.style;
        style.setProperty('--bim-view-tab-text', theme.textPrimary);
        style.setProperty('--bim-view-tab-muted', theme.textSecondary);
        style.setProperty('--bim-view-tab-bg', theme.bgElevated);
    }

    public setLocales(): void {
        void this.refresh();
    }

    public destroy(): void {
        if (this.unsubscribeLocale) {
            this.unsubscribeLocale();
            this.unsubscribeLocale = null;
        }
        if (this.unsubscribeTheme) {
            this.unsubscribeTheme();
            this.unsubscribeTheme = null;
        }
        this.destroyCurrentContent();
        this.element.remove();
    }

    private async refresh(): Promise<void> {
        if (!this.registry || !this.registry.engine3d) {
            return;
        }

        if (this.isRendering) {
            this.pendingRefresh = true;
            return;
        }

        this.isRendering = true;
        const currentToken = ++this.refreshToken;

        try {
            const viewTreeData = this.registry.engine3d.getEngineComponent?.()?.getAllViewTreeData() ?? [];

            const transformedData = transformViewTreeData(viewTreeData);

            if (currentToken !== this.refreshToken) {
                return;
            }

            this.destroyCurrentContent();

            if (transformedData.length === 0) {
                this.renderEmptyState();
                return;
            }

            const tree = new BimTree({
                data: transformedData,
                checkable: false,
                indent: 0,
                enableSearch: true,
                defaultExpandAll: false,
                onNodeSelect: (node) => {
                    const data = node.config.data as { url?: string; viewId?: string; isDefault3D?: boolean } | undefined;

                    // 点击"默认三维"节点：取消剖切 + 回到主视角
                    if (data?.isDefault3D) {
                        this.registry?.engine3d?.getEngineComponent?.()?.deactivateSection();
                        this.registry?.engine3d?.getEngineComponent?.()?.CameraGoHome(500);
                        return;
                    }

                    if (data?.url && data?.viewId) {
                        this.registry?.engine3d?.getEngineComponent?.()?.openView(data.url, data.viewId);
                    }
                },
            });
            tree.init();
            this.tree = tree;
            this.mountElement.appendChild(tree.element);
        } finally {
            this.isRendering = false;
            if (this.pendingRefresh) {
                this.pendingRefresh = false;
                void this.refresh();
            }
        }
    }

    private renderEmptyState(): void {
        const emptyElement = document.createElement('div');
        emptyElement.className = 'component-tree-drawer-view-empty';
        emptyElement.textContent = t('viewTab.empty');
        this.mountElement.appendChild(emptyElement);
    }

    private destroyCurrentContent(): void {
        this.tree?.destroy();
        this.tree = null;
        this.mountElement.innerHTML = '';
    }
}
